import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Flex,
  FlexItem,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { DomainIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useUserContext } from '../../../../contexts/user/UserContext';
import type { PaymentSource } from '../../../../types/models/Payment';
import { useDomainAvailability } from '../../../../hooks/useDomainAvailability';
import { useDomainRegistrationPrices } from '../../../../hooks/useDomainRegistrationPrices';
import { useDnsEndpoints } from '../../../../hooks/useDnsEndpoints';
import { useDomainNames } from '../../../../hooks/useDomainNames';
import { useDomainContact } from '../../../../hooks/useDomainContact';
import { useCreateDomainOrder } from '../../../../hooks/useCreateDomainOrder';
import { useDomainOrderPreview } from '../../../../hooks/useDomainOrderPreview';
import type { DomainAvailabilityResult, DomainOrderCreateRequest } from '../../../../types/models';
import SearchStep from './SearchStep';
import ContactStep from './ContactStep';
import NameserversStep from './NameserversStep';
import RecapStep, { RecapFooter } from './RecapStep';
import {
  EMPTY_CONTACT,
  STEP_IDS,
  areNameserversValid,
  buildContactRequest,
  cleanNameservers,
  isContactDraftValid,
} from './types';
import type { ContactDraft, ContactMode, NsMode } from './types';

const MAX_CART = 25;

const DomainRegistration: React.FunctionComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();

  // --- Read hooks (kept at the page level so their state survives step navigation) ---
  const {
    prices: registrationPrices,
    loading: pricesLoading,
    error: pricesError,
    refetch: refetchPrices,
  } = useDomainRegistrationPrices();
  const { results, loading: searching, error: searchError, hasSearched, search } = useDomainAvailability();
  const { domainNames, loading: domainNamesLoading } = useDomainNames();
  const {
    endpoints,
    loading: endpointsLoading,
    error: endpointsError,
    refetch: refetchEndpoints,
  } = useDnsEndpoints(true);
  const { create, loading: creating } = useCreateDomainOrder();
  const { preview, loading: previewLoading, error: previewError, fetchPreview } = useDomainOrderPreview();

  // --- Wizard state (lifted above <Wizard>) ---
  // Step 1 — search & cart
  const [query, setQuery] = useState('');
  const [selectedTlds, setSelectedTlds] = useState<string[]>([]);
  const [cart, setCart] = useState<DomainAvailabilityResult[]>([]);

  // Step 2 — contact
  const [contactMode, setContactMode] = useState<ContactMode>('new');
  const [contact, setContact] = useState<ContactDraft>(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number>(0);
  const { contact: portfolioContact, loading: portfolioContactLoading } = useDomainContact(selectedPortfolioId);

  // Step 3 — nameservers
  const [nsMode, setNsMode] = useState<NsMode>('endpoint');
  const [endpointId, setEndpointId] = useState<number | null>(null);
  const [hostnames, setHostnames] = useState<string[]>(['', '']);

  // Step 4 — recap
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);
  // Payment source — prepaid balance / saved card / hosted checkout (default). Ignored for a real
  // staff creator (the backend waives the payment), so the payload omits `payment` for staff.
  const [paymentSource, setPaymentSource] = useState<PaymentSource>('checkout');
  const [saveCard, setSaveCard] = useState(false);
  const [paying, setPaying] = useState(false);
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;

  // Apply a portfolio selection to the contact form EXACTLY ONCE per selection. Keying on the
  // applied id (not on the `portfolioContact` reference) is load-bearing: useDomainContact refetches
  // whenever the apiFetch identity changes — which happens on every background OIDC silent token
  // renew — so a reference-keyed effect would silently clobber the user's later edits (and wipe
  // backend field errors). We also wait until the fetch for THIS id has settled (the loaded
  // contact's domain_name_id matches, or it's null), to avoid applying the previous domain's stale
  // contact during an id switch. A picked domain with no saved contact resets the form to empty so
  // the "fill the fields below" hint matches reality.
  const appliedPortfolioId = useRef<number>(0);
  useEffect(() => {
    if (selectedPortfolioId <= 0 || portfolioContactLoading) {
      return;
    }
    const settledForThisId = portfolioContact === null || portfolioContact.domain_name_id === selectedPortfolioId;
    if (!settledForThisId || appliedPortfolioId.current === selectedPortfolioId) {
      return;
    }
    appliedPortfolioId.current = selectedPortfolioId;
    if (portfolioContact) {
      setContact({
        first_name: portfolioContact.first_name ?? '',
        last_name: portfolioContact.last_name ?? '',
        organization: portfolioContact.organization ?? '',
        email: portfolioContact.email ?? '',
        phone: portfolioContact.phone ?? '',
        address_line1: portfolioContact.address_line1 ?? '',
        address_line2: portfolioContact.address_line2 ?? '',
        city: portfolioContact.city ?? '',
        state: portfolioContact.state ?? '',
        zipcode: portfolioContact.zipcode ?? '',
        countrycode: portfolioContact.countrycode ?? '',
      });
    } else {
      setContact(EMPTY_CONTACT);
    }
    setContactErrors({});
  }, [selectedPortfolioId, portfolioContact, portfolioContactLoading]);

  const addToCart = useCallback((result: DomainAvailabilityResult) => {
    setCart((prev) => {
      if (prev.length >= MAX_CART || prev.some((c) => c.domain === result.domain)) {
        return prev;
      }
      return [...prev, result];
    });
  }, []);

  const removeFromCart = useCallback((domain: string) => {
    setCart((prev) => prev.filter((c) => c.domain !== domain));
  }, []);

  const onContactFieldChange = useCallback((field: keyof ContactDraft, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setContactErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const portfolioContactMissing = selectedPortfolioId > 0 && !portfolioContactLoading && portfolioContact === null;

  const selectedEndpoint = endpointId !== null ? (endpoints.find((e) => e.id === endpointId) ?? null) : null;

  // --- Per-step validity (gates the footer's Next / Confirm) ---
  const cartValid = cart.length > 0;
  const contactValid = isContactDraftValid(contact);
  // A managed endpoint must publish ≥2 nameservers (the backend rejects fewer); don't let the user
  // advance past a selection the NS step itself flags as invalid.
  const nsValid =
    nsMode === 'endpoint' ? (selectedEndpoint?.public_nameservers.length ?? 0) >= 2 : areNameserversValid(hostnames);
  const hasUnsupported = preview ? preview.items.some((i) => !i.supported) : false;
  const canSubmit = cartValid && contactValid && nsValid && agreementsAccepted && !hasUnsupported && !creating;

  const handleConfirm = useCallback(async (): Promise<{ jumpToStepId?: string }> => {
    const payload: DomainOrderCreateRequest = {
      domains: cart.map((c) => c.domain),
      contact: buildContactRequest(contact),
      agreements_accepted: true,
      ...(nsMode === 'endpoint'
        ? { dns_endpoint_id: endpointId as number }
        : { nameservers: cleanNameservers(hostnames) }),
      ...(isStaff
        ? {}
        : {
            payment: {
              source: paymentSource,
              ...(paymentSource === 'checkout' && saveCard ? { save_card: true } : {}),
            },
          }),
    };

    const outcome = await create(payload);

    if (outcome.status === 'success') {
      if (outcome.data.payment_waived) {
        // Staff order — the backend waived the Mollie payment (there is no checkout to redirect
        // to): the order is already `paid` and awaits the fulfilment worker; an unpaid invoice
        // will be issued to the client. Land on the shared order status page.
        addNotification({
          title: 'Order created — payment waived (staff)',
          srTitle: 'Success alert',
          description:
            'No online payment is needed for a staff order. Registration will run automatically and an unpaid invoice will be issued to the client.',
          variant: 'success',
        });
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      if (outcome.data.checkout_url === null) {
        // Paid without a redirect: the prepaid balance was debited inside the order transaction
        // (already `paid`), or the saved card was charged in the background (`paid` once Mollie
        // settled it, else the return page's poll sees it flip). Land on the shared status page.
        if (outcome.data.payment_source === 'wallet') {
          addNotification({
            title: 'Order paid from your prepaid balance',
            srTitle: 'Success alert',
            description: 'Your balance was debited and registration starts automatically.',
            variant: 'success',
          });
        } else if (outcome.data.paid) {
          addNotification({
            title: 'Order paid with your saved card',
            srTitle: 'Success alert',
            description: 'Your saved card was charged and registration starts automatically.',
            variant: 'success',
          });
        } else {
          addNotification({
            title: 'Charging your saved card',
            srTitle: 'Info alert',
            description: "Your saved card is being charged — we'll confirm the payment in a moment.",
            variant: 'info',
          });
        }
        setDrawerOpen(true);
        navigate(`/domain-orders/${outcome.data.order_id}/return`);
        return {};
      }
      // Mirror the invoice Pay flow: show the non-dismissible please-wait modal, then hand off to
      // Mollie. Deliberately leave `paying` true — the page is unloading.
      setPaying(true);
      window.location.href = outcome.data.checkout_url;
      return {};
    }

    const fe = outcome.fieldErrors;
    if (fe.contact && typeof fe.contact === 'object') {
      setContactErrors(fe.contact);
    }
    addNotification({
      title: 'Could not create your order',
      srTitle: 'Error alert',
      description: fe.payment ?? outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);

    let jumpToStepId: string | undefined;
    if (fe.domains) {
      jumpToStepId = STEP_IDS.search;
    } else if (fe.contact) {
      jumpToStepId = STEP_IDS.contact;
    } else if (fe.nameservers || fe.dns_endpoint_id) {
      jumpToStepId = STEP_IDS.nameservers;
    } else if (fe.payment) {
      jumpToStepId = STEP_IDS.recap;
    }
    return { jumpToStepId };
  }, [
    cart,
    contact,
    nsMode,
    endpointId,
    hostnames,
    create,
    addNotification,
    setDrawerOpen,
    navigate,
    isStaff,
    paymentSource,
    saveCard,
  ]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="DomainRegisterBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/domains">Domain Names</BreadcrumbItem>
          <BreadcrumbItem isActive>Register a new domain</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <DomainIcon />
          </Icon>
          Register a new domain
        </Content>
        <Content component="p">
          Search for available domains, build a cart, assign a registrant contact and nameservers, then pay.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Wizard navAriaLabel="Domain registration steps" onClose={() => navigate('/datacenter/domains')}>
          <WizardStep name="Search & cart" id={STEP_IDS.search} footer={{ isNextDisabled: !cartValid }}>
            <SearchStep
              registrationPrices={registrationPrices}
              pricesLoading={pricesLoading}
              pricesError={pricesError}
              onRetryPrices={refetchPrices}
              query={query}
              onQueryChange={setQuery}
              selectedTlds={selectedTlds}
              onSelectedTldsChange={setSelectedTlds}
              results={results}
              searching={searching}
              searchError={searchError}
              hasSearched={hasSearched}
              onSearch={search}
              cart={cart}
              onAddToCart={addToCart}
              onRemoveFromCart={removeFromCart}
            />
          </WizardStep>

          <WizardStep name="Contact" id={STEP_IDS.contact} footer={{ isNextDisabled: !contactValid }}>
            <ContactStep
              mode={contactMode}
              onModeChange={setContactMode}
              contact={contact}
              onFieldChange={onContactFieldChange}
              errors={contactErrors}
              domainNames={domainNames}
              domainNamesLoading={domainNamesLoading}
              selectedPortfolioId={selectedPortfolioId}
              onSelectPortfolio={setSelectedPortfolioId}
              portfolioContactLoading={portfolioContactLoading}
              portfolioContactMissing={portfolioContactMissing}
            />
          </WizardStep>

          <WizardStep name="Nameservers" id={STEP_IDS.nameservers} footer={{ isNextDisabled: !nsValid }}>
            <NameserversStep
              mode={nsMode}
              onModeChange={setNsMode}
              endpoints={endpoints}
              endpointsLoading={endpointsLoading}
              endpointsError={endpointsError}
              onRetryEndpoints={refetchEndpoints}
              endpointId={endpointId}
              onEndpointChange={setEndpointId}
              hostnames={hostnames}
              onHostnamesChange={setHostnames}
            />
          </WizardStep>

          <WizardStep
            name="Review & pay"
            id={STEP_IDS.recap}
            footer={<RecapFooter canSubmit={canSubmit} submitting={creating || paying} onConfirm={handleConfirm} />}
          >
            <RecapStep
              cart={cart}
              contact={contact}
              nsMode={nsMode}
              endpoint={selectedEndpoint}
              hostnames={hostnames}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              onRequestPreview={fetchPreview}
              agreementsAccepted={agreementsAccepted}
              onAgreementsChange={setAgreementsAccepted}
              paymentSource={paymentSource}
              onPaymentSourceChange={setPaymentSource}
              saveCard={saveCard}
              onSaveCardChange={setSaveCard}
            />
          </WizardStep>
        </Wizard>
      </PageSection>

      {/* Mirrors the invoice Pay flow: a non-dismissible please-wait modal bridging the click to the
          Mollie redirect. disableFocusTrap is load-bearing — this modal has no focusable element, and
          the focus-trap throws at render with nothing to focus. */}
      <Modal variant="small" isOpen={paying} aria-label="Generating payment" disableFocusTrap>
        <ModalHeader title="Generating your payment" />
        <ModalBody>
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Generating payment" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Preparing your secure payment. You will be redirected to our payment provider in a moment — please keep
                this window open.
              </Content>
            </FlexItem>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DomainRegistration;
